import { useMemo, useState, useEffect, useRef, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, ChevronDown, Search } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Checkbox } from '../../../components/ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '../../../components/ui/popover';
// Direct, not through this directory's barrel — that would be a cycle.
import { BidiText } from './BidiText';
import { KICKER } from '../../styles/typography';

export interface MultiSelectFilterOption<T extends string = string> {
  value: T;
  label: string;
  /**
   * Which `groups` section this option belongs to. Ignored unless `groups` is
   * passed; an option naming no group renders in an implicit leading section,
   * above the first declared heading.
   */
  group?: string;
}

/**
 * A named section of the option list, for a filter over MORE THAN ONE
 * vocabulary.
 *
 * The alternative is a second filter control beside the first, which reads as a
 * second question and — because filters AND — quietly means the opposite of what
 * one picker means: the relationship filter had to offer the built-in
 * relationships and the tenant's own custom ones as one OR'd list (WI ca0962b0).
 *
 * `options` stays the single source of value→label, so the trigger's label, the
 * active-filter chip and the rows cannot disagree about what a value is called.
 */
export interface MultiSelectFilterGroup {
  /** Matches `MultiSelectFilterOption.group`. */
  key: string;
  /**
   * Small heading above the section. Omit it on the FIRST group where the
   * filter's own label already names that vocabulary.
   */
  label?: string;
  /**
   * This section's option labels are TENANT-AUTHORED, so their language is
   * unknown at author time and they need a bidi isolate (`BidiText`).
   *
   * Declared per section rather than for the whole control precisely because a
   * grouped filter is where the two content classes meet: the built-in section
   * is our own `t()` copy, written in the UI locale, and wrapping THAT is the
   * mistake STYLE_GUIDE § Bidirectional Text names — it is what put "PanLuma" on
   * the wrong side of its own Hebrew sentence. The tenant's own relationship
   * class names, in the section below it, are Class A and do need one.
   */
  userAuthoredLabels?: boolean;
}

/** Special value used to represent "no value" / blank entries */
export const BLANK_FILTER_VALUE = '__blank__';

export interface MultiSelectFilterProps<T extends string = string> {
  /** All available options */
  options: MultiSelectFilterOption<T>[];
  /**
   * Divides the option list into named sections, in this order.
   *
   * Presentation only: the selection stays one flat array, "All"/"Clear" still
   * cover every option, and a search still filters across the whole list (the
   * headings simply follow the surviving rows). Omit it and the list renders
   * exactly as it always has.
   */
  groups?: MultiSelectFilterGroup[];
  /** Currently selected values */
  selected: T[];
  /** Callback when selection changes */
  onChange: (selected: T[]) => void;
  /** Label shown when all items are selected (e.g., "All statuses") */
  allLabel: string;
  /** Label shown when multiple items are selected (e.g., "{{count}} statuses") - uses i18n interpolation */
  countLabel: string;
  /** Whether the popover is open (controlled) */
  open?: boolean;
  /** Callback when popover open state changes */
  onOpenChange?: (open: boolean) => void;
  /** Additional class name for the trigger button */
  className?: string;
  /** Width of the trigger button */
  width?: string;
  /** Include a "No value" option for filtering items with blank/null values */
  includeBlankOption?: boolean;
  /** Show a search input to filter options (useful for long lists) */
  searchable?: boolean;
  /**
   * Opt in to FOCUS-on-click from the "all" state: clicking an option while every
   * box is checked selects ONLY that option, instead of unchecking it.
   *
   * The default (`false`) is the checkbox affordance the control draws — all
   * checked, click one, that one unchecks — and it is what the AR invoice-list
   * payment filter needs, where the question is "which payment states do I want"
   * and excluding one of three is the common move (WI 6ac704a1 / fc5de37f15).
   *
   * A SCOPE filter asks a different question. From "All Modules" the dominant
   * intent is narrowing to ONE module — the Gmail/Slack scope-chip gesture, which
   * focuses rather than excludes — and exclude-one-of-N stays reachable in two
   * clicks (focus one, then add the others). So it is a per-consumer decision and
   * not a global one: today only `GlobalSearchFilters` passes it (WI a0b7e5b1).
   */
  focusFromAll?: boolean;
  /**
   * Accessible name for the trigger. Needed wherever the control sits under a
   * form `Label` rather than in a filter bar: the trigger only announces its
   * current VALUE ("All departments"), which tells a screen-reader user nothing
   * about which field they are in.
   */
  'aria-label'?: string;
}

/**
 * A multi-select filter dropdown with "All" and "None" shortcuts.
 *
 * Features:
 * - Checkbox list for selecting multiple options
 * - "All" button to select all options
 * - "None" button to clear selection
 * - Shows "All {type}" when nothing or everything is selected
 * - Shows single item label when one item is selected
 * - Shows "{count} {type}" when multiple (but not all) items are selected
 */
export function MultiSelectFilter<T extends string = string>({
  options,
  groups,
  selected,
  onChange,
  allLabel,
  countLabel,
  open,
  onOpenChange,
  className,
  width = 'w-[160px]',
  includeBlankOption = false,
  searchable = false,
  focusFromAll = false,
  'aria-label': ariaLabel,
}: MultiSelectFilterProps<T>) {
  const { t } = useTranslation('common');
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [internalOpen, setInternalOpen] = useState(false);
  const isOpen = open ?? internalOpen;
  const handleOpenChange = (value: boolean) => {
    setInternalOpen(value);
    onOpenChange?.(value);
  };

  // Build the full list of values including blank option if enabled
  const allValues = useMemo(() => {
    const values = options.map(o => o.value);
    if (includeBlankOption) {
      values.unshift(BLANK_FILTER_VALUE as T);
    }
    return values;
  }, [options, includeBlankOption]);

  // Filter options by search query
  const filteredOptions = useMemo(() => {
    if (!searchQuery) return options;
    const q = searchQuery.toLowerCase();
    return options.filter(o => o.label.toLowerCase().includes(q));
  }, [options, searchQuery]);

  /**
   * The grouped layout, resolved once: the implicit leading section (options
   * naming no group) and the declared sections that still have rows.
   *
   * Resolved here rather than inline so the divider rule below can ask "did
   * anything render above me?" — which needs the SURVIVING group list, not the
   * declared one, since a search can empty a section.
   */
  const leadingOptions = useMemo(
    () => (groups ? filteredOptions.filter((o) => !o.group) : []),
    [groups, filteredOptions],
  );
  const renderedGroups = useMemo(
    () =>
      (groups ?? [])
        .map((group) => ({
          group,
          options: filteredOptions.filter((o) => o.group === group.key),
        }))
        // A section with nothing left after a search would otherwise render a
        // heading over blank space.
        .filter((section) => section.options.length > 0),
    [groups, filteredOptions],
  );

  // Track if user explicitly clicked "None" - this is a temporary UI state
  // that shows all checkboxes unchecked until any other interaction
  const [showNoneState, setShowNoneState] = useState(false);

  // Reset the "none" state when selected changes externally (e.g., URL change, page load)
  useEffect(() => {
    if (selected.length > 0) {
      setShowNoneState(false);
    }
  }, [selected]);

  // Clear search and focus input when popover opens
  useEffect(() => {
    if (isOpen && searchable) {
      setSearchQuery('');
      setTimeout(() => searchInputRef.current?.focus(), 0);
    }
  }, [isOpen, searchable]);

  // Both empty (without showNoneState) and full array mean "all" for filtering.
  // Declared here rather than beside the render flags below because `toggleOption`
  // branches on it: the two "all" spellings are indistinguishable on screen (every
  // box checked, trigger reads the all-label), so a click must mean the same thing
  // in both.
  const isAllSelected =
    (selected.length === 0 && !showNoneState) || selected.length === allValues.length;

  const toggleOption = (value: T) => {
    // Scope-filter semantics, opt-in per consumer: from the all state, clicking an
    // option FOCUSES it. See the `focusFromAll` prop doc for which question each
    // semantic answers.
    if (focusFromAll && isAllSelected) {
      setShowNoneState(false);
      onChange([value]);
      return;
    }
    // Phantom "all checked" (empty array === all, when the user hasn't clicked
    // "Clear") must toggle from the FULL set, so clicking a box UNCHECKS it —
    // matching the checkbox affordance. Toggling from the empty array instead
    // would select ONLY the clicked value (the inversion bug).
    const base = selected.length === 0 && !showNoneState ? allValues : selected;
    const newSelected = base.includes(value)
      ? base.filter(v => v !== value)
      : [...base, value];
    // Unchecking the LAST checked box yields [] — which otherwise renders as the
    // phantom "all checked" state again (every box flips back to checked). Treat
    // that exactly like clicking "Clear": stay in the explicit none-state so the
    // boxes remain visually unchecked. Any other toggle clears the none-state.
    setShowNoneState(newSelected.length === 0);
    onChange(newSelected);
  };

  /**
   * One option row. Hoisted out of the map so the grouped and ungrouped paths
   * render the SAME row — a second copy is how the phantom-all checked state
   * comes to be computed differently on one path.
   */
  const renderOption = (
    option: MultiSelectFilterOption<T>,
    userAuthoredLabel = false,
  ) => {
    // Show checked if:
    // - explicitly in the selected array, OR
    // - nothing is selected AND user didn't explicitly click "None"
    const isSelected = selected.includes(option.value) ||
      (selected.length === 0 && !showNoneState);
    return (
      <div
        key={option.value}
        className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-accent cursor-pointer"
        onClick={() => toggleOption(option.value)}
      >
        <Checkbox
          checked={isSelected}
          onCheckedChange={() => toggleOption(option.value)}
        />
        <span className="text-sm flex-1">
          {userAuthoredLabel ? <BidiText>{option.label}</BidiText> : option.label}
        </span>
        {isSelected && (
          <Check className="h-4 w-4 text-primary shrink-0" />
        )}
      </div>
    );
  };

  const selectAll = () => {
    setShowNoneState(false);
    onChange(allValues);
  };

  const selectNone = () => {
    setShowNoneState(true); // Mark that user explicitly clicked "None"
    onChange([]);
  };

  // Get display label based on selection state
  const getDisplayLabel = (): ReactNode => {
    // Nothing selected OR everything selected = show "All {type}"
    if (selected.length === 0 || selected.length === allValues.length) {
      return allLabel;
    }
    // Single item selected = show that item's label
    if (selected.length === 1) {
      // Check if it's the blank option
      if (selected[0] === BLANK_FILTER_VALUE) {
        return t('app.noValue');
      }
      const option = options.find(o => o.value === selected[0]);
      const label = option?.label || selected[0];
      // The one branch that can render TENANT-AUTHORED text: every other arm is
      // our own `t()` copy and must stay bare (STYLE_GUIDE § Bidirectional Text).
      const userAuthored = groups?.find((g) => g.key === option?.group)?.userAuthoredLabels;
      return userAuthored ? <BidiText>{label}</BidiText> : label;
    }
    // Multiple items selected = show count
    return t(countLabel, { count: selected.length });
  };

  // "All" button disabled when all checkboxes are already shown as checked
  const isAllDisabled = selected.length === allValues.length || (selected.length === 0 && !showNoneState);
  // "None" button disabled when in the "none" state (all unchecked)
  const isNoneDisabled = showNoneState;

  return (
    <Popover open={isOpen} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          aria-label={ariaLabel}
          className={`${width} justify-between ${className || ''}`}
        >
          <span className="truncate">{getDisplayLabel()}</span>
          <ChevronDown className="h-4 w-4 opacity-50 shrink-0 ms-2" />
        </Button>
      </PopoverTrigger>
      {/* The `var()` caps the panel to the space Radix measured between the
          trigger and the viewport edge; the options list's own `max-h-*`
          below stays as the design ceiling and shrinks under that cap. */}
      <PopoverContent
        className="flex max-h-[var(--radix-popover-content-available-height)] w-[200px] flex-col p-0"
        align="start"
      >
        {/* Search input */}
        {searchable && (
          <div className="p-2 border-b">
            <div className="flex items-center gap-2 rounded-md border border-input bg-background px-2 py-1">
              <Search className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
              <input
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={t('app.search')}
                className="flex-1 text-sm bg-transparent outline-none placeholder:text-muted-foreground"
              />
            </div>
          </div>
        )}
        {/* All / Clear shortcuts */}
        <div className="flex items-center justify-between p-2 border-b">
          <Button
            variant={isAllSelected ? 'secondary' : 'ghost'}
            size="sm"
            className="h-7 text-xs px-3"
            onClick={selectAll}
            disabled={isAllDisabled}
          >
            {t('app.all')}
          </Button>
          <button
            type="button"
            className="text-xs text-muted-foreground hover:text-foreground disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={selectNone}
            disabled={isNoneDisabled}
          >
            {t('app.clear')}
          </button>
        </div>
        {/* Options list */}
        <div className="min-h-0 max-h-[300px] flex-1 space-y-1 overflow-y-auto p-2">
          {/* Blank option at the top if enabled */}
          {includeBlankOption && !searchQuery && (
            <>
              {(() => {
                const blankValue = BLANK_FILTER_VALUE as T;
                const isBlankSelected = selected.includes(blankValue) ||
                  (selected.length === 0 && !showNoneState);
                return (
                  <div
                    className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-accent cursor-pointer"
                    onClick={() => toggleOption(blankValue)}
                  >
                    <Checkbox
                      checked={isBlankSelected}
                      onCheckedChange={() => toggleOption(blankValue)}
                    />
                    <span className="text-sm flex-1 italic text-muted-foreground">{t('app.noValue')}</span>
                    {isBlankSelected && (
                      <Check className="h-4 w-4 text-primary shrink-0" />
                    )}
                  </div>
                );
              })()}
              <div className="border-b my-1" />
            </>
          )}
          {groups ? (
            <>
              {/* An option naming no group leads, above the first heading. */}
              {leadingOptions.map((option) => renderOption(option))}
              {renderedGroups.map(({ group, options: groupOptions }, index) => (
                <div key={group.key} className="space-y-1">
                  {/* One line BETWEEN vocabularies — this is ONE filter, so the
                      divider is what separates them, not a second control. It is
                      drawn only when something actually rendered above it: an
                      unconditional line would sit directly under the All/Clear
                      row's own border and read as a stray rule. `index` counts
                      SURVIVING groups, so a search that empties the first one
                      does not leave the second drawing a divider under nothing. */}
                  {(leadingOptions.length > 0 || index > 0) && (
                    <div className="border-b my-1" />
                  )}
                  {group.label && (
                    <p className={`${KICKER} px-2 pt-1`}>{group.label}</p>
                  )}
                  {groupOptions.map((option) =>
                    renderOption(option, group.userAuthoredLabels),
                  )}
                </div>
              ))}
            </>
          ) : (
            filteredOptions.map((option) => renderOption(option))
          )}
          {searchable && filteredOptions.length === 0 && (
            <p className="text-sm text-muted-foreground text-center py-2">{t('app.noResults')}</p>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
